import type { ConsumableCreateRequest } from '@quromedical/models';
import { ConsumableListForm } from 'core/consumable';
import { Alert, Skeleton, SkeletonProvider, Snackbar } from 'design-system';
import { useFiniteState } from 'hooks/useFiniteState';
import { useRevalidation } from 'hooks/useRevalidation';
import type { ConsumableFetcher } from 'integration';
import type React from 'react';
import { useCallback } from 'react';
import { strings } from 'strings';

type FormState = 'editing' | 'submitting' | 'error';

export interface ConsumableCreateProps {
  onSubmit: (consumableUsage: ConsumableCreateRequest) => Promise<void>;
  consumableFetcher: ConsumableFetcher;
  title: string;
  patient: string;
  admission: string;
}

export const CreateConsumableCard: React.FC<ConsumableCreateProps> = ({
  onSubmit,
  consumableFetcher,
  title,
  admission,
  patient,
}) => {
  const state = useFiniteState<FormState>('editing');
  const revalidate = useRevalidation('Consumable');

  const handleSubmit = useCallback(
    async (data: ConsumableCreateRequest) => {
      state.set('submitting');

      try {
        await onSubmit(data);
        state.set('editing');
        revalidate();
      } catch (err) {
        state.set('error');
      }
    },
    [revalidate, onSubmit, state],
  );

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <ConsumableListForm
            handleSubmit={handleSubmit}
            consumableFetcher={consumableFetcher}
            title={title}
            admission={admission}
            patient={patient}
          />
        </Skeleton>
      </SkeletonProvider>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
